/** Determines where a user should resume in a learning path. */

import { CourseWithProgress, LessonWithProgress } from '@/hooks/useUserLearningPaths';
import { CMSLearningPath } from '@/types/cms-lp';
import { UserLearningPath } from '@/types/learning-paths';
import { computeLpAssessmentUnlockedFromMergedCourses, computeLpAssessmentUnlocked } from '@/lib/utils/learning-path-utils';

interface GetResumeUrlParams {
  learningPathData: CMSLearningPath;
  userLpData: UserLearningPath | null;
  courses: CourseWithProgress[];
}

interface GetSimpleResumeUrlParams {
  learningPathUrl: string;
  userLpData: UserLearningPath | null;
  hasAssessment?: boolean;
}

/** Resume URL using full course/lesson progress (e.g. learning path hero page). */
export function getResumeUrl({ learningPathData, userLpData, courses }: GetResumeUrlParams): string {
  const learningPathUrl = learningPathData?.url?.replace('/', '');
  const defaultUrl = `/learning-paths/${learningPathUrl}`;

  if (!userLpData?.isSubscribed || !courses || courses.length === 0) {
    return defaultUrl;
  }

  if (userLpData.progress?.isCompleted) {
    return defaultUrl;
  }

  // Assessment unlocked (course/LP rules) but LP not fully completed → assessment
  const hasAssessment = learningPathData?.is_assessment === true;
  const assessmentUnlocked = computeLpAssessmentUnlockedFromMergedCourses(userLpData, courses);
  if (hasAssessment && assessmentUnlocked) {
    return `${defaultUrl}/assessment`;
  }

  // Find the first incomplete lesson across all courses
  for (const course of courses) {
    const courseUrl = course.url;
    if (typeof courseUrl !== 'string' || courseUrl.length === 0) continue;

    if (!course.chapters || course.chapters.length === 0) continue;

    const normalizedCourseUrl = courseUrl.startsWith('/') ? courseUrl : `/${courseUrl}`;

    for (const chapter of course.chapters) {
      const lessons = chapter.lessons?.lessons || [];

      for (const lesson of lessons) {
        const lessonWithProgress = lesson as LessonWithProgress;

        if (!lessonWithProgress.isCompleted) {
          const lessonUrl = lesson.url;
          if (typeof lessonUrl === 'string' && lessonUrl.length > 0) {
            const normalizedLessonUrl = lessonUrl.startsWith('/') ? lessonUrl : `/${lessonUrl}`;
            return `/learning-paths/${learningPathUrl}${normalizedCourseUrl}${normalizedLessonUrl}`;
          }
          // lesson.url can be missing on shallow CMS references — fall back to course overview
          return `/learning-paths/${learningPathUrl}${normalizedCourseUrl}`;
        }
      }
    }
  }

  return defaultUrl;
}

/** Resume URL using only course-level progress (e.g. learning path cards on homepage). */
export function getSimpleResumeUrl({ learningPathUrl, userLpData, hasAssessment }: GetSimpleResumeUrlParams): string {
  const cleanLpUrl = learningPathUrl?.replace('/', '');
  const defaultUrl = `/learning-paths/${cleanLpUrl}`;

  if (!userLpData?.isSubscribed || !userLpData?.courses || userLpData.courses.length === 0) {
    return defaultUrl;
  }

  if (userLpData.progress?.isCompleted) {
    return defaultUrl;
  }

  const assessmentUnlocked = computeLpAssessmentUnlocked(userLpData);
  if (hasAssessment && assessmentUnlocked) {
    return `${defaultUrl}/assessment`;
  }

  // No lesson-level data here, so just point at the LP URL even if a course is incomplete
  const firstIncompleteCourse = userLpData.courses.find(
    (course) => !course.progress?.isCompleted
  );

  return defaultUrl;
}
